import React, { memo, useState } from 'react';
import { Bot, Send, Sparkles } from 'lucide-react';
import { PaneShell } from './PaneShell';
import { cn } from '../../lib/utils';

/**
 * AdvisorPane — the prompt line.
 *
 * This was a hero card: a 10px avatar, a title, a subtitle, a pulsing "Online"
 * dot, and about 180px of vertical space to hold one text input. In a console
 * that space belongs to the queue above it, and the "Online" indicator was
 * decoration — it was hardcoded, and reported nothing.
 *
 * What survives is what the widget was actually for: a place to start a
 * conversation without leaving Home. It sits at the foot of the command column
 * because that is where a prompt goes.
 *
 * Sending creates a session and navigates into it, so this composer is
 * genuinely a launcher, not a second chat surface.
 */

const SUGGESTIONS = ['Help me plan', 'Review my code', 'Explain architecture'];

function AdvisorPaneImpl({ onSend, isSending }) {
    const [message, setMessage] = useState('');

    const submit = (e) => {
        e.preventDefault();
        const trimmed = message.trim();
        if (!trimmed || isSending) return;
        onSend(trimmed);
        setMessage('');
    };

    return (
        <PaneShell icon={Bot} label="Advisor" accent="text-primary" scroll={false}>
            <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col gap-2 p-3">
                <div className="relative min-h-0 flex-1">
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => {
                            // Enter sends, Shift+Enter breaks — the convention every
                            // composer in this app already uses.
                            if (e.key === 'Enter' && !e.shiftKey) submit(e);
                        }}
                        placeholder="Ask anything about this project…"
                        aria-label="Ask the advisor"
                        data-testid="home-advisor-input"
                        disabled={isSending}
                        className="h-full min-h-[56px] w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] p-2.5 pr-11 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/40 focus:bg-white/[0.05] disabled:opacity-60"
                    />
                    <button
                        type="submit"
                        disabled={isSending || !message.trim()}
                        aria-label="Send"
                        className={cn(
                            'absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                            message.trim() && !isSending
                                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                                : 'bg-white/5 text-muted-foreground/50'
                        )}
                    >
                        {isSending
                            ? <Sparkles className="h-4 w-4 animate-spin" />
                            : <Send className="h-4 w-4" />}
                    </button>
                </div>

                {/* Wraps rather than scrolls: at a short pane height these are the
                    first thing that should give way, not the input. */}
                <div className="flex flex-shrink-0 flex-wrap gap-1.5">
                    {SUGGESTIONS.map((suggestion) => (
                        <button
                            key={suggestion}
                            type="button"
                            onClick={() => setMessage(suggestion)}
                            className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-foreground"
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            </form>
        </PaneShell>
    );
}

export const AdvisorPane = memo(AdvisorPaneImpl);
export default AdvisorPane;
